import { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import 'styles/BlockComponent.css'
import 'styles/TunnelComponent.css'
import 'styles/WindowComponent.css'
import 'styles/RowComponent.css'
import 'styles/PlantComponent.css'
import TunnelComponent from 'components/common/TunnelComponent'
import { makeRequest } from 'services/request.service'
import { deriveTunnelWindows, tunnelSideDonePercent } from 'utils/blockWindows'
import { useAuth } from 'auth/AuthContext'

const todayStr = () => new Date().toISOString().split('T')[0]

// Linear blend between two hex colors (t in 0..1) — used to tint a tunnel by its
// activity-progress ratio (base color at 0%, done color at 100%).
const mixHex = (from, to, t) => {
  const f = from.replace('#', '')
  const g = to.replace('#', '')
  const fr = parseInt(f.slice(0, 2), 16)
  const fg = parseInt(f.slice(2, 4), 16)
  const fb = parseInt(f.slice(4, 6), 16)
  const tr = parseInt(g.slice(0, 2), 16)
  const tg = parseInt(g.slice(2, 4), 16)
  const tb = parseInt(g.slice(4, 6), 16)
  const r = Math.round(fr + (tr - fr) * t)
  const gg = Math.round(fg + (tg - fg) * t)
  const b = Math.round(fb + (tb - fb) * t)
  return `rgb(${r}, ${gg}, ${b})`
}

// Progress uses 3 tiers so partial work reads clearly (the old gray→green blend
// washed out): GRAY = every line pending, YELLOW (a light→amber gradient by how
// much is done) = some lines done and some pending, GREEN = fully complete.
const PROGRESS_BASE = '#eef2f7' // gray — all pending
const PROGRESS_DONE = '#22c55e' // green — 100% done
const PROGRESS_PARTIAL_LOW = '#fef08a' // light yellow — barely started
const PROGRESS_PARTIAL_HIGH = '#f59e0b' // amber — almost done
// import PlantComponent from 'components/common/PlantComponent'

const PLANT_STATES = {
  HEALTHY: 'healthy',
  DISEASE: 'disease',
  NUTRIENT_DEFICIENCY: 'nutrient-deficiency'
}

const VIEWS = {
  overview: 'overview',
  tunnel: 'tunnel',
  row: 'row'
}

const ZOOM_MODES = {
  compact: 'compact',
  normal: 'normal',
  detailed: 'detailed'
}

const getZoomStorageKey = (farmingBlockId) => `block-map-zoom-mode-${farmingBlockId}`

const isZoomMode = (value) => Object.values(ZOOM_MODES).includes(value)

const normalizeBlockConfig = (configs) => {
  return (configs || []).map(config => ({
    ...config,
    numberOfTunnels: Number(config.numberOfTunnels || 0),
    numberOfWindows: Number(config.numberOfWindows || 0)
  }))
}

const getTotalTunnels = (configs) => {
  return configs.reduce((sum, config) => sum + Number(config.numberOfTunnels || 0), 0)
}

const getMaxWindows = (configs) => {
  return Math.max(...configs.map(config => Number(config.numberOfWindows || 0)), 0)
}

const getMinWindows = (configs) => {
  if (!configs.length) return 0
  return Math.min(...configs.map(config => Number(config.numberOfWindows || 0)))
}

const getDefaultZoomMode = (configs) => {
  return getTotalTunnels(configs) >= 24 || getMaxWindows(configs) >= 24
    ? ZOOM_MODES.compact
    : ZOOM_MODES.normal
}

const getStoredZoomMode = (farmingBlockId) => {
  if (typeof window === 'undefined' || farmingBlockId === undefined || farmingBlockId === null) return null

  const storedZoomMode = window.localStorage.getItem(getZoomStorageKey(farmingBlockId))
  return isZoomMode(storedZoomMode) ? storedZoomMode : null
}

const getInitialZoomMode = (configs, farmingBlockId) => {
  return getStoredZoomMode(farmingBlockId) || getDefaultZoomMode(configs)
}

const BlockMapComponent = ({
  accessTunnel = 1,
  accessWindow = 1,
  blockConfig = [],
  farmingBlockId,
  isHydroponicBlock,
  toggleIsTunnelRowModalOpen,
  tunnelRefreshRef,
  onBlockConfigChange
}) => {
  const { user } = useAuth()
  const initialBlockConfig = normalizeBlockConfig(blockConfig)
  const [editableBlockConfig, setEditableBlockConfig] = useState(initialBlockConfig)
  // Activity-progress overlay: pick an activity + day and the map colors each line
  // (block_row) that was scanned. Empty selection => plain base map.
  const [activities, setActivities] = useState([])
  const [progressActivityId, setProgressActivityId] = useState('')
  const [progressDate, setProgressDate] = useState(todayStr())
  const [doneRowIds, setDoneRowIds] = useState(() => new Set())
  // Once the user picks an activity/date manually we stop auto-defaulting.
  const userTouchedProgress = useRef(false)
  // Block rows drive the per-side window counts (plants -> windows) so the map
  // reflects the real sub-blocks instead of the single block-level access_window.
  const [blockRows, setBlockRows] = useState([])
  const [state, setState] = useState({
    selectedTunnel: null,
    selectedConfigIndex: null,
    hoveredTunnelIndex: null,
    hoveredWindowLabel: '',
    zoomMode: getInitialZoomMode(initialBlockConfig, farmingBlockId),
    selectedRow: null,
    currentView: VIEWS.overview,
    draggedItem: null,
    numberOfWindows: 0,
    numberOfRows: 0,
    plants: [],
    plantsPerRow: 0,
    selectedFarmingBlockId: ''
    // scale: 1,
  })

  // const {
  //   selectedTunnel,
  //   selectedRow,
  //   currentView,
  //   numberOfWindows
  //   // plants, numberOfRows, plantsPerRow
  // } = state

  // const [scale, setScale] = useState(1)
  // const [rowStates, setRowStates] = useState({})

  // const handleRowClick = (rowIndex) => {
  //   setState((s) => ({
  //     ...s,
  //     selectedRow: rowIndex,
  //     currentView: VIEWS.row
  //   }))
  // }

  // const handleZoomIn = () => {
  //   setScale(prev => Math.min(prev + 0.1, 2.0))
  // }

  // const handleZoomOut = () => {
  //   setScale(prev => Math.max(prev - 0.1, 0.2))
  // }

  // const handleDragStart = (e, status) => {
  //   e.dataTransfer.setData('status', status)
  // }

  // const handleDragOver = (e) => {
  //   e.preventDefault()
  // }

  // const handleDrop = (e, plantIndex) => {
  //   e.preventDefault()
  //   const newStatus = e.dataTransfer.getData('status')
  //   const _plants = plants.map((plant, index) =>
  //     index === plantIndex
  //       ? { ...plant, status: newStatus }
  //       : plant
  //   )
  //   setState((s) => ({
  //     ...s,
  //     plants: _plants
  //   }))
  // }

  // const handleRowStatus = (rowIndex, status) => {
  //   if (status === 'reset') {
  //     setRowStates({}) // Limpiar estados
  //     return
  //   }

  //   if (rowIndex !== null && rowIndex !== undefined) {
  //     setRowStates(prev => ({
  //       ...prev,
  //       [rowIndex]: status
  //     }))
  //   }
  // }

  const handleTunnelClick = (
    tunnelIndex,
    configIndex,
    numberOfWindows,
    numberOfRows,
    plantsPerWindow,
    isAccessTunnel
  ) => {
    if (isAccessTunnel) return

    const _plantsPerRow = Array.from(
      { length: plantsPerWindow * numberOfWindows || 0 },
      (_, i) => ({
        id: `plant-${i}`,
        number: i + 1,
        status: PLANT_STATES.HEALTHY
      })
    )

    setState(s => ({
      ...s,
      selectedTunnel: tunnelIndex,
      selectedConfigIndex: configIndex,
      selectedRow: null,
      currentView: VIEWS.tunnel,
      numberOfWindows,
      numberOfRows,
      plants: _plantsPerRow,
      selectedFarmingBlockId: farmingBlockId
    }))
  }

  const applyBlockConfigChange = (nextConfig) => {
    const normalizedConfig = normalizeBlockConfig(nextConfig)
    setEditableBlockConfig(normalizedConfig)

    if (typeof onBlockConfigChange === 'function') {
      onBlockConfigChange(normalizedConfig)
    }
  }

  const changeAllWindows = (amount) => {
    const minWindows = getMinWindows(editableBlockConfig)
    if (amount < 0 && minWindows <= accessWindow) return

    applyBlockConfigChange(
      editableBlockConfig.map(config => ({
        ...config,
        numberOfWindows: Math.max(accessWindow, Number(config.numberOfWindows || 0) + amount)
      }))
    )
  }

  const changeTunnelLines = (amount) => {
    if (!editableBlockConfig.length) {
      applyBlockConfigChange([{ numberOfTunnels: 1, numberOfWindows: Math.max(accessWindow, 1) }])
      return
    }

    const totalTunnels = getTotalTunnels(editableBlockConfig)
    if (amount < 0 && totalTunnels <= accessTunnel) return

    const nextConfig = editableBlockConfig.map(config => ({ ...config }))

    if (amount > 0) {
      const lastIndex = nextConfig.length - 1
      nextConfig[lastIndex].numberOfTunnels = Number(nextConfig[lastIndex].numberOfTunnels || 0) + 1
    } else {
      let targetIndex = -1
      for (let i = nextConfig.length - 1; i >= 0; i -= 1) {
        if (Number(nextConfig[i].numberOfTunnels || 0) > 0) {
          targetIndex = i
          break
        }
      }
      if (targetIndex === -1) return
      nextConfig[targetIndex].numberOfTunnels = Number(nextConfig[targetIndex].numberOfTunnels || 0) - 1
    }

    applyBlockConfigChange(nextConfig)
  }

  const changeSelectedTunnelWindows = (nextWindowCount) => {
    if (state.selectedConfigIndex === null) return

    const safeWindowCount = Math.max(accessWindow, nextWindowCount)
    const nextConfig = editableBlockConfig.map((config, index) => (
      index === state.selectedConfigIndex
        ? { ...config, numberOfWindows: safeWindowCount }
        : { ...config }
    ))

    setState(s => ({
      ...s,
      numberOfWindows: safeWindowCount
    }))
    applyBlockConfigChange(nextConfig)
  }

  const handleZoomModeChange = (e) => {
    const nextZoomMode = e.target.value
    if (!isZoomMode(nextZoomMode)) return

    if (typeof window !== 'undefined' && farmingBlockId !== undefined && farmingBlockId !== null) {
      window.localStorage.setItem(getZoomStorageKey(farmingBlockId), nextZoomMode)
    }

    setState(s => ({
      ...s,
      zoomMode: nextZoomMode
    }))
  }

  const setHoveredGridPosition = (tunnelIndex, windowLabel = '') => {
    setState(s => ({
      ...s,
      hoveredTunnelIndex: tunnelIndex,
      hoveredWindowLabel: windowLabel
    }))
  }

  const clearHoveredGridPosition = () => {
    setState(s => ({
      ...s,
      hoveredTunnelIndex: null,
      hoveredWindowLabel: ''
    }))
  }

  const renderBreadcrumbs = () => {
    return (
      <div className='breadcrumbs'>
        <span
          className={`crumb ${state.currentView === 'overview' ? 'active' : ''}`}
          onClick={() => {
            setState((s) => ({
              ...s,
              currentView: VIEWS.overview,
              selectedTunnel: null,
              selectedConfigIndex: null,
              selectedRow: null
            }))
          }}
        >
          Block Map
        </span>
        {state.selectedTunnel !== null && (
          <>
            <span className='separator'>/</span>
            <span
              className={`crumb ${state.currentView === 'tunnel' ? 'active' : ''}`}
              onClick={() =>
                setState(
                  (s) => ({
                    ...s,
                    currentView: VIEWS.tunnel,
                    selectedRow: null
                  }))}
            >
              Tunnel {state.selectedTunnel + 1}
            </span>
          </>
        )}
        {state.selectedRow !== null && (
          <>
            <span className='separator'>/</span>
            <span
              className={`crumb ${state.currentView === 'row' ? 'active' : ''}`}
              onClick={() => setState(
                (s) => ({
                  ...s,
                  currentView: VIEWS.row
                }))}
            >
              Row {state.selectedRow + 1}
            </span>
          </>
        )}
      </div>
    )
  }

  const dragRef = useRef(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  // event listeners for mouse drag, scroll
  // to enable horizontal scrolling on the block content area
  // this is a workaround for the overflow-x-auto
  useEffect(() => {
    const normalizedConfig = normalizeBlockConfig(blockConfig)
    setEditableBlockConfig(normalizedConfig)
    setState(s => ({
      ...s,
      zoomMode: getInitialZoomMode(normalizedConfig, farmingBlockId),
      hoveredTunnelIndex: null,
      hoveredWindowLabel: ''
    }))
  }, [blockConfig, farmingBlockId])

  // Fetch the block rows to derive per-side windows from plant counts. Exposed as
  // a callback so the tunnel detail can trigger a re-fetch after adding a line.
  const fetchBlockRows = useCallback(async () => {
    if (!farmingBlockId) {
      setBlockRows([])
      return
    }
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/${farmingBlockId}/rows`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setBlockRows(Array.isArray(response?.data) ? response.data : [])
    } catch (error) {
      setBlockRows([])
    }
  }, [farmingBlockId])

  useEffect(() => { fetchBlockRows() }, [fetchBlockRows])

  // Load the farm's activities for the progress selector.
  useEffect(() => {
    if (!user?.farmId) return
    let cancelled = false
    makeRequest({
      method: 'GET',
      url: `/management/activities/${user.farmId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })
      .then(res => { if (!cancelled) setActivities(Array.isArray(res?.data) ? res.data : []) })
      .catch(() => { if (!cancelled) setActivities([]) })
    return () => { cancelled = true }
  }, [user?.farmId])

  // Default the progress view to the block's most recent scanned activity + day
  // (so a sector with data shows it without the user hunting for the right day),
  // unless the user has already chosen manually.
  useEffect(() => {
    if (!farmingBlockId) return
    userTouchedProgress.current = false
    let cancelled = false
    makeRequest({
      method: 'GET',
      url: `/management/farming-blocks/${farmingBlockId}/latest-scan`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })
      .then(res => {
        if (cancelled || userTouchedProgress.current) return
        const { activityId, date } = res?.data || {}
        if (activityId) setProgressActivityId(String(activityId))
        if (date) setProgressDate(date)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [farmingBlockId])

  // Fetch which lines were scanned for the selected activity + day.
  useEffect(() => {
    if (!farmingBlockId || !progressActivityId || !progressDate) {
      setDoneRowIds(new Set())
      return
    }
    let cancelled = false
    makeRequest({
      method: 'GET',
      url: `/management/farming-blocks/${farmingBlockId}/progress?activityId=${progressActivityId}&date=${progressDate}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })
      .then(res => { if (!cancelled) setDoneRowIds(new Set(res?.data?.doneRowIds || [])) })
      .catch(() => { if (!cancelled) setDoneRowIds(new Set()) })
    return () => { cancelled = true }
  }, [farmingBlockId, progressActivityId, progressDate])

  useEffect(() => {
    const slider = dragRef.current
    if (!slider) return

    const handleMouseDown = e => {
      isDragging.current = true
      startX.current = e.pageX
      scrollLeft.current = slider.scrollLeft
      slider.classList.add('dragging')
    }

    const handleMouseMove = e => {
      if (!isDragging.current) return
      e.preventDefault()
      const x = e.pageX
      const walk = (x - startX.current) * 1.5
      slider.scrollLeft = scrollLeft.current - walk
    }

    const stopDragging = () => {
      isDragging.current = false
      slider.classList.remove('dragging')
    }

    slider.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', stopDragging)

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', stopDragging)
    }
  }, [])

  return (
    <div className='block-container'>
      <div className='block-header'>
        {renderBreadcrumbs()}
        {/* <div className='zoom-actions'>
          <button
            className='zoom-btn'
            onClick={handleZoomOut}
            disabled={scale <= 0.2}
          >
            −
          </button>
          <span className='zoom-level'>{Math.round(scale * 100)}%</span>
          <button
            className='zoom-btn'
            onClick={handleZoomIn}
            disabled={scale >= 2.0}
          >
            +
          </button>
        </div> */}
      </div>

      <div className='block-content-wrapper overflow-x-auto w-full max-w-6xl mx-auto'>
        <div
          ref={dragRef}
          className='block-content  cursor-grab active:cursor-grabbing select-none'
          // style={{
          //   transform: state.scale < 1 ? `scale(${state.scale})` : 'none',
          //   transformOrigin: 'top left',
          //   transition: 'transform 0.2s ease',
          //   width: state.scale >= 1 ? `${state.scale * 100}%` : '100%',
          //   maxWidth: state.scale >= 1 ? 'none' : '100%'
          // }}
        >
          {/* Vista General */}
          {state.currentView === VIEWS.overview && (() => {
            const tunnels = editableBlockConfig.flatMap((fbConfig, configIndex) => {
              const tunnelOffset = editableBlockConfig
                .slice(0, configIndex)
                .reduce((sum, config) => sum + config.numberOfTunnels, 0)
              return Array.from({ length: fbConfig.numberOfTunnels }, (_, i) => ({
                tunnelIndex: tunnelOffset + i,
                configIndex,
                fbConfig,
                isAccessTunnel: accessTunnel === tunnelOffset + i + 1
              }))
            })

            const configMaxWindows = getMaxWindows(editableBlockConfig)
            const totalTunnels = getTotalTunnels(editableBlockConfig)
            const minWindows = getMinWindows(editableBlockConfig)

            // Derive per-tunnel windows from plant counts. When available, the grid
            // is anchored on the access row (accessRow = deepest SUB_A) so every
            // tunnel aligns on the walkway and each side shows its real window
            // count. Otherwise fall back to the block-level access_window geometry.
            const tw = deriveTunnelWindows(blockRows, isHydroponicBlock)
            const useDerived = tw.hasData
            const accessIdx = useDerived ? tw.maxSubA : accessWindow - 1
            const gridRows = useDerived ? tw.maxSubA + 1 + tw.maxSubB : configMaxWindows
            const maxWindows = gridRows

            // Activity-progress tint per tunnel SIDE (SUB_A / SUB_B independently),
            // only when an activity is selected.
            const showProgress = !!progressActivityId
            const donePct = showProgress ? tunnelSideDonePercent(blockRows, doneRowIds) : null
            // Overall block completion (% of lines done) for the selected activity + day.
            const totalLines = blockRows.length
            const doneLines = totalLines ? blockRows.reduce((n, r) => n + (doneRowIds.has(r.rowId) ? 1 : 0), 0) : 0
            const progressPct = showProgress && totalLines ? Math.round((doneLines / totalLines) * 100) : null
            const tunnelTint = (tunnelIndex, side) => {
              if (!showProgress) return undefined
              const pct = donePct.get(`${tunnelIndex + 1}|${side || 'FULL'}`) || 0
              if (pct <= 0) return { backgroundColor: PROGRESS_BASE } // gray: all pending
              if (pct >= 1) return { backgroundColor: PROGRESS_DONE } // green: fully done
              // yellow tier: some done, some pending — shade by how much.
              return { backgroundColor: mixHex(PROGRESS_PARTIAL_LOW, PROGRESS_PARTIAL_HIGH, pct) }
            }

            const coordinateLabel = state.hoveredTunnelIndex !== null
              ? `T${state.hoveredTunnelIndex + 1}${state.hoveredWindowLabel ? ` · ${state.hoveredWindowLabel}` : ''}`
              : 'Hover grid'

            const getLabel = (j) => {
              if (j < accessIdx) return `W${accessIdx - j}`
              if (j === accessIdx) return 'A'
              return `W${j - accessIdx}`
            }

            // Per-cell geometry: which rows a given tunnel occupies, whether a row
            // is its access, and which sub-block side it belongs to.
            const cellInfo = (tunnelIndex, fbConfig, j, isAccessTunnel) => {
              if (!useDerived) {
                const hasWindow = j < fbConfig.numberOfWindows
                return { hasWindow, isAccessRow: j === accessIdx, side: null, isWalkwayCell: false }
              }
              const w = tw.byTunnel.get(tunnelIndex + 1)
              const subA = w ? w.subA : 0
              const subB = w ? w.subB : 0
              const full = w ? w.full : 0

              // Access tunnel: the side(s) with NO plants are the physical walkway,
              // so paint them as access (blue) for the full height of that side. The
              // side WITH plants (e.g. G23's SUB_B) renders as normal windows.
              if (isAccessTunnel) {
                if (j === accessIdx) return { hasWindow: true, isAccessRow: true, side: null, isWalkwayCell: false }
                if (j < accessIdx) {
                  if (subA === 0) return { hasWindow: true, isAccessRow: false, side: 'A', isWalkwayCell: true }
                  return { hasWindow: j >= accessIdx - subA, isAccessRow: false, side: 'A', isWalkwayCell: false }
                }
                if (subB === 0) return { hasWindow: true, isAccessRow: false, side: 'B', isWalkwayCell: true }
                return { hasWindow: j <= accessIdx + subB, isAccessRow: false, side: 'B', isWalkwayCell: false }
              }

              if (!w) return { hasWindow: false, isAccessRow: false, side: null, isWalkwayCell: false }
              if (subA === 0 && subB === 0) {
                // FULL tunnel (no sub-block split): windows hang below the access row.
                return { hasWindow: j > accessIdx && j <= accessIdx + full, isAccessRow: false, side: null, isWalkwayCell: false }
              }
              const top = accessIdx - subA
              const bottom = accessIdx + subB
              return {
                hasWindow: j >= top && j <= bottom,
                isAccessRow: j === accessIdx,
                side: j < accessIdx ? 'A' : j > accessIdx ? 'B' : null,
                isWalkwayCell: false
              }
            }

            return (
              <div className='overview-section'>
                <div className='block-map-toolbar sticky-grid-toolbar' aria-label='Block map toolbar'>
                  {typeof onBlockConfigChange === 'function' && (
                    <div className='block-map-controls' aria-label='Block map grid controls'>
                      <div className='block-map-control-group'>
                        <span className='grid-control-label'>Tunnel lines</span>
                        <button
                          type='button'
                          aria-label='Remove tunnel line from block map'
                          title='Remove tunnel line'
                          disabled={totalTunnels <= accessTunnel}
                          onClick={() => changeTunnelLines(-1)}
                          className='grid-control-button'
                        >
                          <MinusIcon className='w-4 h-4' />
                        </button>
                        <span className='grid-control-value'>{totalTunnels}</span>
                        <button
                          type='button'
                          aria-label='Add tunnel line to block map'
                          title='Add tunnel line'
                          onClick={() => changeTunnelLines(1)}
                          className='grid-control-button add'
                        >
                          <PlusIcon className='w-4 h-4' />
                        </button>
                      </div>
                      <div className='block-map-control-group'>
                        <span className='grid-control-label'>Windows</span>
                        <button
                          type='button'
                          aria-label='Remove window from block map'
                          title='Remove window'
                          disabled={minWindows <= accessWindow}
                          onClick={() => changeAllWindows(-1)}
                          className='grid-control-button'
                        >
                          <MinusIcon className='w-4 h-4' />
                        </button>
                        <span className='grid-control-value'>{maxWindows}</span>
                        <button
                          type='button'
                          aria-label='Add window to block map'
                          title='Add window'
                          onClick={() => changeAllWindows(1)}
                          className='grid-control-button add'
                        >
                          <PlusIcon className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className='block-map-progress-control'>
                    <label className='grid-control-label' htmlFor='block-map-activity'>Activity</label>
                    <select
                      id='block-map-activity'
                      aria-label='Block map progress activity'
                      value={progressActivityId}
                      onChange={(e) => { userTouchedProgress.current = true; setProgressActivityId(e.target.value) }}
                      className='block-map-zoom-select'
                    >
                      <option value=''>None</option>
                      {activities.map(a => (
                        <option key={a.activityId} value={a.activityId}>{a.activityName}</option>
                      ))}
                    </select>
                    <input
                      type='date'
                      aria-label='Block map progress date'
                      value={progressDate}
                      max={todayStr()}
                      onChange={(e) => { userTouchedProgress.current = true; setProgressDate(e.target.value) }}
                      className='block-map-zoom-select'
                    />
                    {progressPct !== null && (
                      <span className='block-map-progress-pct' aria-live='polite'>{progressPct}% complete</span>
                    )}
                  </div>
                  <div className='block-map-zoom-control'>
                    <label className='grid-control-label' htmlFor='block-map-zoom'>Zoom</label>
                    <select
                      id='block-map-zoom'
                      aria-label='Block map zoom'
                      value={state.zoomMode}
                      onChange={handleZoomModeChange}
                      className='block-map-zoom-select'
                    >
                      <option value={ZOOM_MODES.compact}>Compact</option>
                      <option value={ZOOM_MODES.normal}>Normal</option>
                      <option value={ZOOM_MODES.detailed}>Detailed</option>
                    </select>
                  </div>
                  <div className='block-map-coordinate-badge' aria-live='polite'>
                    {coordinateLabel}
                  </div>
                </div>
                <div
                  className={`overview-table block-map-zoom-${state.zoomMode}`}
                  style={{ '--overview-tunnel-count': tunnels.length }}
                  onMouseLeave={clearHoveredGridPosition}
                >
                  {/* Header: tunnel numbers */}
                  <div className='overview-header-row'>
                    <div className='overview-row-corner sticky-grid-corner' />
                    {tunnels.map(({ tunnelIndex, isAccessTunnel }) => (
                      <div
                        key={`th-${tunnelIndex}`}
                        className={`${isAccessTunnel ? 'overview-col-header-access' : 'overview-col-header'} sticky-tunnel-header ${state.hoveredTunnelIndex === tunnelIndex ? 'overview-tunnel-active' : ''}`}
                        onMouseEnter={() => setHoveredGridPosition(tunnelIndex)}
                        onMouseLeave={clearHoveredGridPosition}
                      >
                        T{tunnelIndex + 1}
                      </div>
                    ))}
                  </div>

                  {/* Window rows */}
                  {Array.from({ length: maxWindows }, (_, j) => {
                    const isAccessRow = j === accessIdx
                    const windowLabel = getLabel(j)
                    return (
                      <div key={`row-${j}`} className='overview-window-row'>
                        <div className={`overview-row-label${isAccessRow ? '-access' : ''} sticky-window-label`}>
                          {windowLabel}
                        </div>
                        {tunnels.map(({ tunnelIndex, configIndex, fbConfig, isAccessTunnel }) => {
                          const info = cellInfo(tunnelIndex, fbConfig, j, isAccessTunnel)
                          const hasWindow = info.hasWindow
                          // Access = the horizontal access row, or a walkway cell (the
                          // access tunnel's side that has no plants). Planted cells —
                          // including the access tunnel's planted side (G23 SUB_B) —
                          // render as normal windows and stay interactive.
                          const isAccess = hasWindow && (info.isAccessRow || info.isWalkwayCell)
                          const cellLabel = `T${tunnelIndex + 1} ${windowLabel}`
                          // Progress tint (per tunnel side) on windowed, non-access cells.
                          const cellStyle = (hasWindow && !isAccess) ? tunnelTint(tunnelIndex, info.side) : undefined
                          return (
                            <div
                              key={`cell-${tunnelIndex}-${j}`}
                              aria-label={cellLabel}
                              tabIndex={hasWindow && !isAccess ? 0 : -1}
                              style={cellStyle}
                              className={`${
                                !hasWindow
                                  ? 'overview-cell-empty'
                                  : isAccess
                                    ? 'overview-cell-access'
                                    : 'overview-cell'
                              } ${state.hoveredTunnelIndex === tunnelIndex ? 'overview-tunnel-active' : ''}`}
                              onMouseEnter={() => setHoveredGridPosition(tunnelIndex, windowLabel)}
                              onMouseLeave={clearHoveredGridPosition}
                              onFocus={() => setHoveredGridPosition(tunnelIndex, windowLabel)}
                              onBlur={clearHoveredGridPosition}
                              onClick={() => {
                                // Only planted (non-access) cells open the tunnel; the
                                // access tunnel opens from its planted side (G23 SUB_B).
                                if (!hasWindow || isAccess) return
                                const w = useDerived ? tw.byTunnel.get(tunnelIndex + 1) : null
                                const derivedTotal = w
                                  ? (w.subA || w.subB ? w.subA + 1 + w.subB : w.full)
                                  : fbConfig.numberOfWindows
                                handleTunnelClick(
                                  tunnelIndex,
                                  configIndex,
                                  derivedTotal,
                                  fbConfig.numberOfRows,
                                  fbConfig.plantsPerRow,
                                  false
                                )
                              }}
                            />
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {/* Vista Detallada de Túnel */}
          {state.currentView === VIEWS.tunnel && state.selectedTunnel !== null && (
            <div className='tunnel-detail-view'>
              <TunnelComponent
                farmingBlockId={state.selectedFarmingBlockId}
                tunnelNumber={state.selectedTunnel + 1}
                numberOfWindows={state.numberOfWindows}
                accessWindow={accessWindow}
                isHydroponicBlock={isHydroponicBlock}
                doneRowIds={doneRowIds}
                toggleIsTunnelRowModalOpen={toggleIsTunnelRowModalOpen}
                onExposeRefresh={(refreshFn) => {
                  tunnelRefreshRef.current = refreshFn
                }}
                onWindowCountChange={changeSelectedTunnelWindows}
                onDataChanged={fetchBlockRows}
                // rowStates={rowStates}
                // onRowStatus={handleRowStatus}
                // accessTunnel={accessTunnel}
                // onRowClick={handleRowClick}
              />
            </div>
          )}

          {/* Vista de Plantas en Fila */}
          {/* {currentView === VIEWS.row && selectedRow !== null && (
            <div className='plants-view'>
              <div className='section-header'>
                <div className='section-title'>
                  <h4>Row {selectedRow + 1}</h4>
                  <span className='tunnel-reference'>Tunnel {selectedTunnel + 1}</span>
                </div>
                <div className='row-stats'>
                  <div className='stat-badge'>
                    <span className='stat-value'>{plantsPerRow}</span>
                    <span className='stat-label'>Plants</span>
                  </div>
                </div>
              </div>

              <div className='status-markers'>
                <div
                  className='status-marker healthy'
                  draggable
                  onDragStart={(e) => handleDragStart(e, PLANT_STATES.HEALTHY)}
                >
                  Healthy
                </div>
                <div
                  className='status-marker disease'
                  draggable
                  onDragStart={(e) => handleDragStart(e, PLANT_STATES.DISEASE)}
                >
                  Disease
                </div>
                <div
                  className='status-marker nutrient-deficiency'
                  draggable
                  onDragStart={(e) => handleDragStart(e, PLANT_STATES.NUTRIENT_DEFICIENCY)}
                >
                  Nutrient Deficiency
                </div>
              </div>

              <div className='plants-container'>
                <div className='plants-grid'>
                  {plants.map((plant, index) => (
                    <div
                      key={plant.id}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className='plant-wrapper'
                    >
                      <PlantComponent
                        plantNumber={plant.number}
                        status={plant.status}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  )
}

BlockMapComponent.propTypes = {
  blockConfig: PropTypes.arrayOf(
    PropTypes.shape({
      numberOfTunnels: PropTypes.number.isRequired,
      numberOfWindows: PropTypes.number.isRequired,
      numberOfRows: PropTypes.number,
      plantsPerWindow: PropTypes.number
    })
  ).isRequired,
  farmingBlockId: PropTypes.number.isRequired,
  toggleIsTunnelRowModalOpen: PropTypes.func.isRequired,
  accessWindow: PropTypes.number.isRequired,
  accessTunnel: PropTypes.number.isRequired,
  isHydroponicBlock: PropTypes.bool,
  tunnelRefreshRef: PropTypes.shape({
    current: PropTypes.any
  }),
  onBlockConfigChange: PropTypes.func
}

export default BlockMapComponent
